import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../../../utils/debugLogger";
import { getConnectedWordPressSites, publishBlogToWordPress } from "./wordpressBlogService";

/**
 * SGL-66 publishing: every place a Brand's blog can go (its WordPress.com sites
 * and the Sagelyn-hosted blog on its own domain), the Brand's saved Authors, and
 * the server renderer's preview. One post is live at ONE destination at a time;
 * the backend enforces it so two copies never compete for the canonical URL.
 */

const CONTENT_API_BASE = process.env.REACT_APP_CONTENT_API_URL || "";

function headers(idToken: string) {
  return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
}

function toError(error: unknown, fallback: string): Error {
  debug.error(`${fallback}:`, formatSafeErrorMessage(error));
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    const msg = data?.error || data?.message;
    if (msg) return new Error(msg);
  }
  return error instanceof Error ? error : new Error(fallback);
}

function base(customerId: string, idToken: string): string {
  if (!customerId?.trim()) throw new Error("Customer ID is required");
  if (!idToken?.trim()) throw new Error("Authentication token is required");
  if (!CONTENT_API_BASE) throw new Error("REACT_APP_CONTENT_API_URL is not set.");
  return `${CONTENT_API_BASE}/${customerId}`;
}

// ── Hosted blog (Squarespace, Wix, any site builder) ─────────────────────────

export type HostedStatus = "AWAITING_DNS" | "VALIDATING" | "CONNECTED" | "FAILED" | "DISCONNECTED";

export interface HostedBlog {
  connected: boolean;
  destinationId?: string;
  hostname?: string;
  status?: HostedStatus;
  siteTitle?: string;
  homeUrl?: string;
  dnsRecord?: { type: string; name: string; value: string };
  /** What public DNS says right now, in words the owner can act on (pending only). */
  dns?: { ok: boolean; message: string; code?: string; provider?: string };
  error?: string;
  /** Why a pending connection is waiting once DNS is right: "securing" (certificate) or "previous_connection". */
  waitReason?: string;
}

export const isHostedPending = (h?: HostedBlog | null): boolean =>
  Boolean(h?.status && (h.status === "AWAITING_DNS" || h.status === "VALIDATING"));

/** GET /content/{customer_id}/hosted-blog/domain. A pending one is advanced on read. */
export const getHostedBlog = async (customerId: string, idToken: string): Promise<HostedBlog> => {
  try {
    const { data } = await axios.get<HostedBlog>(`${base(customerId, idToken)}/hosted-blog/domain`, {
      headers: headers(idToken),
    });
    return { ...data, connected: Boolean(data?.destinationId) && data.status !== "DISCONNECTED" };
  } catch (error) {
    throw toError(error, "Failed to load your blog connection");
  }
};

/** POST /content/{customer_id}/hosted-blog/domain {hostname, site_title, home_url}. */
export const connectHostedBlog = async (
  customerId: string,
  idToken: string,
  input: { hostname: string; siteTitle: string; homeUrl: string }
): Promise<HostedBlog> => {
  try {
    const { data } = await axios.post<HostedBlog>(
      `${base(customerId, idToken)}/hosted-blog/domain`,
      { hostname: input.hostname.trim().toLowerCase(), site_title: input.siteTitle, home_url: input.homeUrl },
      { headers: headers(idToken) }
    );
    return { ...data, connected: true };
  } catch (error) {
    throw toError(error, "Failed to connect your blog");
  }
};

/** DELETE /content/{customer_id}/hosted-blog/domain. Published pages stay stored. */
export const disconnectHostedBlog = async (customerId: string, idToken: string): Promise<void> => {
  try {
    await axios.delete(`${base(customerId, idToken)}/hosted-blog/domain`, { headers: headers(idToken) });
  } catch (error) {
    throw toError(error, "Failed to disconnect your blog");
  }
};

// ── Shopify store blog (SGL-431) ─────────────────────────────────────────────
// A custom Shopify app, so only Brands on the backend allowlist see the card
// (`available`). Errors come back as 400/409/502 with `error` text, never
// 401/403/402, so a Shopify problem can't sign the person out.

export type ShopifyStatus = "CONNECTED" | "CHOOSE_BLOG";

export interface ShopifyBlogRef {
  id: string;
  title: string;
  handle?: string;
}

export interface ShopifyConnection {
  connected: boolean;
  /** Whether this Brand may connect a Shopify store at all. */
  available: boolean;
  destinationId?: string;
  status?: ShopifyStatus;
  /** The store's `*.myshopify.com` address. */
  shop?: string;
  shopName?: string;
  storefrontUrl?: string;
  blog?: ShopifyBlogRef | null;
  blogs?: ShopifyBlogRef[];
  /** Exchange only: the store has one blog, so it was picked for the owner. */
  autoChosen?: boolean;
}

/** GET /content/{customer_id}/shopify. A stack without the route reads as unavailable. */
export const getShopifyConnection = async (customerId: string, idToken: string): Promise<ShopifyConnection> => {
  try {
    const { data } = await axios.get<ShopifyConnection>(`${base(customerId, idToken)}/shopify`, {
      headers: headers(idToken),
    });
    return { ...data, connected: Boolean(data?.connected), available: Boolean(data?.available) };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return { connected: false, available: false };
    throw toError(error, "Failed to load your Shopify connection");
  }
};

/** POST /content/{customer_id}/shopify/oauth/authorize-url. The backend builds Shopify's install URL. */
export const getShopifyAuthorizeUrl = async (
  customerId: string,
  idToken: string,
  input: { shop: string; state: string; redirectUri: string }
): Promise<{ url: string; shop: string }> => {
  try {
    const { data } = await axios.post<{ url: string; shop: string }>(
      `${base(customerId, idToken)}/shopify/oauth/authorize-url`,
      { shop: input.shop.trim().toLowerCase(), state: input.state, redirect_uri: input.redirectUri },
      { headers: headers(idToken) }
    );
    return data;
  } catch (error) {
    throw toError(error, "Failed to start connecting Shopify");
  }
};

/** POST /content/{customer_id}/shopify/oauth/exchange-token. `params` = every callback query param, unchanged (HMAC). */
export const exchangeShopifyCode = async (
  customerId: string,
  idToken: string,
  params: Record<string, string>
): Promise<ShopifyConnection> => {
  try {
    const { data } = await axios.post<ShopifyConnection>(
      `${base(customerId, idToken)}/shopify/oauth/exchange-token`,
      { params },
      { headers: headers(idToken) }
    );
    return { ...data, connected: true, available: true };
  } catch (error) {
    throw toError(error, "Failed to connect Shopify");
  }
};

/** PUT /content/{customer_id}/shopify/blog {blog_id}. Refused while posts are live on the current blog. */
export const chooseShopifyBlog = async (customerId: string, idToken: string, blogId: string): Promise<ShopifyConnection> => {
  try {
    const { data } = await axios.put<ShopifyConnection>(
      `${base(customerId, idToken)}/shopify/blog`,
      { blog_id: blogId },
      { headers: headers(idToken) }
    );
    return { ...data, connected: true, available: Boolean(data?.available) };
  } catch (error) {
    throw toError(error, "Failed to choose the blog");
  }
};

/** DELETE /content/{customer_id}/shopify. Articles already live stay on the store (`stillLive`). */
export const disconnectShopify = async (customerId: string, idToken: string): Promise<{ stillLive: number }> => {
  try {
    const { data } = await axios.delete<{ stillLive?: number }>(`${base(customerId, idToken)}/shopify`, {
      headers: headers(idToken),
    });
    return { stillLive: data?.stillLive ?? 0 };
  } catch (error) {
    throw toError(error, "Failed to disconnect Shopify");
  }
};

/** "https://www.x.com/blogs/news" → "www.x.com/blogs/news". */
export const shopifyBlogUrl = (s: ShopifyConnection): string =>
  s.storefrontUrl && s.blog?.handle ? `${s.storefrontUrl.replace(/\/$/, "")}/blogs/${s.blog.handle}` : s.storefrontUrl || "";

// ── Destinations ─────────────────────────────────────────────────────────────

export interface BlogDestination {
  /** `wp_<siteId>` for WordPress, `dst_…` for the hosted blog, `shp_…` for Shopify (Publications key). */
  id: string;
  type: "wordpress" | "hosted" | "shopify";
  label: string;
  url: string;
  /** Can't be published to yet: hosted waits for DNS, Shopify for a blog to be chosen. */
  pending?: boolean;
  /** Why it is pending, as the badge says it. */
  pendingLabel?: string;
  /** WordPress only: the site id the WordPress routes take. */
  wordpressSiteId?: string;
}

export const wordpressDestinationId = (siteId: string) => `wp_${siteId}`;

/** Every place this Brand can publish a blog, connected or still connecting. */
export const listBlogDestinations = async (
  customerId: string,
  idToken: string
): Promise<{ destinations: BlogDestination[]; hosted: HostedBlog; hostedError?: string; shopifyError?: string }> => {
  // WordPress must keep working even if the hosted-blog or Shopify lookup
  // fails; each failure is returned (and shown), never swallowed. A Shopify-only
  // Brand would otherwise look like it has nowhere to publish.
  const [sites, hostedRes, shopRes] = await Promise.all([
    getConnectedWordPressSites(customerId, idToken),
    getHostedBlog(customerId, idToken).then(
      (h) => ({ h, err: "" }),
      (e: unknown) => ({ h: { connected: false } as HostedBlog, err: e instanceof Error ? e.message : "Failed to load your blog" })
    ),
    getShopifyConnection(customerId, idToken).then(
      (s) => ({ s, err: "" }),
      (e: unknown) => ({
        s: { connected: false, available: false } as ShopifyConnection,
        err: e instanceof Error ? e.message : "Failed to load your Shopify connection",
      })
    ),
  ]);
  const hosted = hostedRes.h;
  const shop = shopRes.s;
  const destinations: BlogDestination[] = sites.map((s) => ({
    id: wordpressDestinationId(s.id),
    type: "wordpress",
    label: (s.url || s.name).replace(/^https?:\/\//, "").replace(/\/$/, ""),
    url: s.url,
    wordpressSiteId: s.id,
  }));
  if (hosted.connected && hosted.destinationId && hosted.hostname) {
    destinations.unshift({
      id: hosted.destinationId,
      type: "hosted",
      label: hosted.hostname,
      url: `https://${hosted.hostname}/`,
      pending: hosted.status !== "CONNECTED",
      pendingLabel: "Waiting for DNS",
    });
  }
  if (shop.connected && shop.destinationId) {
    const blogUrl = shopifyBlogUrl(shop);
    destinations.push({
      id: shop.destinationId,
      type: "shopify",
      label: (blogUrl || shop.shopName || shop.shop || "Shopify").replace(/^https?:\/\//, ""),
      url: blogUrl,
      pending: shop.status !== "CONNECTED",
      pendingLabel: "Choose a blog",
    });
  }
  return {
    destinations,
    hosted,
    ...(hostedRes.err ? { hostedError: hostedRes.err } : {}),
    ...(shopRes.err ? { shopifyError: shopRes.err } : {}),
  };
};

/** Publish a saved post now. WordPress keeps its own route; everything else uses the generic one. */
export const publishToDestination = async (
  customerId: string,
  idToken: string,
  postId: string,
  destination: BlogDestination
): Promise<{ url: string }> => {
  if (destination.type === "wordpress" && destination.wordpressSiteId) {
    const res = await publishBlogToWordPress(customerId, postId, destination.wordpressSiteId, idToken);
    return { url: res.wordpress_url };
  }
  try {
    const { data } = await axios.post<{ url: string }>(
      `${base(customerId, idToken)}/blogs/${postId}/publish`,
      { destination_id: destination.id },
      { headers: headers(idToken) }
    );
    return { url: data.url };
  } catch (error) {
    throw toError(error, "Failed to publish");
  }
};

/** Take the post down from one site. Answers once the page is actually gone. */
export const unpublishFromDestination = async (
  customerId: string,
  idToken: string,
  postId: string,
  destinationId: string
): Promise<void> => {
  try {
    await axios.post(
      `${base(customerId, idToken)}/blogs/${postId}/unpublish`,
      { destination_id: destinationId },
      { headers: headers(idToken) }
    );
  } catch (error) {
    throw toError(error, "Failed to unpublish");
  }
};

// ── Authors ──────────────────────────────────────────────────────────────────

export interface BlogAuthor {
  id: string;
  name: string;
  kind: "person" | "organization";
  jobTitle?: string;
  bio?: string;
  linkedin?: string;
  website?: string;
  isDefault: boolean;
}

export const listAuthors = async (customerId: string, idToken: string): Promise<BlogAuthor[]> => {
  try {
    const { data } = await axios.get<{ authors: BlogAuthor[] }>(`${base(customerId, idToken)}/authors`, {
      headers: headers(idToken),
    });
    return data.authors || [];
  } catch (error) {
    throw toError(error, "Failed to load authors");
  }
};

export const createAuthor = async (
  customerId: string,
  idToken: string,
  input: { name: string; kind: "person" | "organization"; job_title?: string; bio?: string; linkedin?: string; is_default?: boolean }
): Promise<BlogAuthor> => {
  try {
    const { data } = await axios.post<{ author: BlogAuthor }>(`${base(customerId, idToken)}/authors`, input, {
      headers: headers(idToken),
    });
    return data.author;
  } catch (error) {
    throw toError(error, "Failed to save the author");
  }
};

// ── Preview (the same server renderer every destination uses) ────────────────

export interface RenderedPreview {
  html: string;
  /** Things a destination may refuse: images without a description, direct image links, … */
  problems: string[];
}

export const previewDraft = async (
  customerId: string,
  idToken: string,
  draft: Record<string, unknown>
): Promise<RenderedPreview> => {
  try {
    const { data } = await axios.post<RenderedPreview>(`${base(customerId, idToken)}/blogs/preview`, draft, {
      headers: headers(idToken),
    });
    return { html: data.html || "", problems: data.problems || [] };
  } catch (error) {
    throw toError(error, "Failed to build the preview");
  }
};
